/**
 * What screens can DO (UiActions) and what they can TALK TO (UiServices): the platform-neutral contract. The web
 * provides them through Preact contexts (src/ui/actions.ts `useActions()` / `useServices()`); a native shell through
 * its own. Implemented by the app shell (web: src/app/boot.ts).
 */
import type { AudioEngine } from '@hf/audio';
import type { InputManager } from '../input/types';
import type { OnlineClient } from '../net/types';
import type { RenderHost } from '@hf/render-core/types';
import type { MatchSetup, ScreenId } from './types';

export interface UiActions {
  /**
   * Navigate to a screen. The ONLY way to change screens: it runs the SceneManager's exit/enter hooks (e.g. leaving
   * the match stops its loop, renderer, driver and listeners).
   */
  goto(screen: ScreenId): void;
  /** Start a match (offline or online) with a full setup; navigates to the match screen. */
  startMatch(setup: MatchSetup): void;
  /** Leave the current match (confirmations are the caller's job) and return to the main menu. */
  quitMatch(): void;
  /** Open the pause overlay. Offline drivers stop ticking; online play keeps running (the overlay is cosmetic). */
  pause(): void;
  /** Close the pause/move-list overlay and resume. */
  resume(): void;
  /** Same setup, fresh seed (online: offers a rematch to the opponent). */
  rematch(): void;
  /** Leave the results screen (back to the menu that started the match). */
  continueFromResults(): void;
  /** Unlock WebAudio from a user gesture. */
  unlockAudio(): void;
  /** Practice: re-spot the ball at the practice start with a fresh down (also R / pad Back in the match). */
  practiceReset(): void;
  /** Practice: switch between practising offense and defense (also Tab / pad right-stick click). */
  practiceSwapSides(): void;
  /** Tutorial: skip the current step (also N in the match). */
  tutorialSkip(): void;
  /** Tutorial: restart Football 101 from the first lesson. */
  tutorialRestart(): void;
}

/** State of a replay theater (Results "TOP PLAYS · INSTANT REPLAY"). */
export type ReplayTheaterState = 'loading' | 'playing' | 'ended' | 'error';

/** A running replay theater: a renderer inside a screen element playing recorded clips back to back. */
export interface ReplayTheaterHandle {
  /** Jump to clip `index` of the list it was opened with (wraps). */
  play(index: number): void;
  /** Stop the tape and free the renderer (idempotent). */
  close(): void;
}

/** Instant replays of the last match (implemented by src/app; screens never see the renderer or the sim). */
export interface ReplayService {
  /**
   * Play clips (`ui.replays` ids) back to back inside `el` (a renderer canvas is attached to it), starting at
   * `start`. `onClip` reports the clip on tape; `onState` loading → playing → ended (or error).
   */
  open(
    el: RenderHost,
    ids: readonly number[],
    start: number,
    events: { onClip?(index: number): void; onState?(state: ReplayTheaterState): void },
  ): ReplayTheaterHandle;
}

/** Long-lived services screens may use directly (all created once at boot). */
export interface UiServices {
  /** Devices, menu navigation (`onMenu`), rebinding. */
  input: InputManager;
  /** Lobby, queues, rooms, rating, leaderboard. */
  online: OnlineClient;
  /** Live volume preview, UI click SFX, menu music. */
  audio: AudioEngine;
  /** Instant replays of the last match (absent in tests / tools: the reel then shows static cards). */
  replays?: ReplayService;
}

const noop = (): void => {};
export const noopActions: UiActions = {
  goto: noop,
  startMatch: noop,
  quitMatch: noop,
  pause: noop,
  resume: noop,
  rematch: noop,
  continueFromResults: noop,
  unlockAudio: noop,
  practiceReset: noop,
  practiceSwapSides: noop,
  tutorialSkip: noop,
  tutorialRestart: noop,
};
